/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativos, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaVestimenta.module.scss";

// =====================================================
// DATOS IPSOS VESTIMENTA (Base: 400 casos)
// =====================================================
const IPSOS_VESTIMENTA = {
  laboral: {
    frecuenciaCompra: {
      semanal: { pct: 0.018, factor: 52, label: "Semanalmente" },
      mensual: { pct: 0.398, factor: 12, label: "Mensualmente" },
      trimestral: { pct: 0.257, factor: 4, label: "Trimestralmente" },
      semestral: { pct: 0.214, factor: 2, label: "Semestralmente" },
      anual: { pct: 0.097, factor: 1, label: "Anualmente" },
      noCompra: { pct: 0.015, factor: 0, label: "No compra" },
    },
    marcas: {
      "H&M": 0.242,
      Zara: 0.155,
      Arrow: 0.064,
      Otros: 0.064,
      Tommy: 0.048,
      Levis: 0.048,
      Docker: 0.045,
      Polo: 0.038,
    },
    dondeCompra: {
      "Centros comerciales": 0.931,
      "Centro de la ciudad": 0.088,
      "Extranjero vía web": 0.045,
      "Página web": 0.025,
      "No compra": 0.015,
    },
    canalPreferido: {
      "Tiendas físicas": 0.816,
      Online: 0.184,
    },
    tipoVestimenta: {
      Casual: 0.69,
      Formal: 0.299,
      Uniforme: 0.026,
    },
  },
  personal: {
    frecuenciaCompra: {
      semanal: { pct: 0.027, factor: 52, label: "Semanalmente" },
      mensual: { pct: 0.536, factor: 12, label: "Mensualmente" },
      trimestral: { pct: 0.216, factor: 4, label: "Trimestralmente" },
      semestral: { pct: 0.144, factor: 2, label: "Semestralmente" },
      anual: { pct: 0.077, factor: 1, label: "Anualmente" },
    },
    marcas: {
      "H&M": 0.164,
      Zara: 0.125,
      Levis: 0.122,
      Io: 0.056,
      "American Eagle": 0.053,
      Falabella: 0.048,
      Sibilla: 0.046,
      Otros: 0.045,
    },
    dondeCompra: {
      "Centros comerciales": 0.906,
      "Centro de la ciudad": 0.115,
      "Extranjero vía web": 0.096,
      "Sitios web": 0.03,
    },
    canalPreferido: {
      "Tiendas físicas": 0.721,
      Online: 0.279,
    },
  },
};

// Opciones de ticket promedio (CLP)
const TICKETS_VESTIMENTA = [30000, 50000, 75000, 100000];

// Escenarios de captación
const ESCENARIOS_CAPTACION = [2, 4, 6, 8, 10];

export const Vestimenta = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  const [vistaActiva, setVistaActiva] = useState("laboral");
  const [marcaSeleccionada, setMarcaSeleccionada] = useState("H&M");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(4);
  const [ticketPromedio, setTicketPromedio] = useState(50000);
  const [verAnual, setVerAnual] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativos({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // Reset marca seleccionada al cambiar de vista
  useEffect(() => {
    if (vistaActiva === "mix") {
      setMarcaSeleccionada("H&M");
    } else {
      const primeraMarca = Object.keys(IPSOS_VESTIMENTA[vistaActiva].marcas)[0];
      setMarcaSeleccionada(primeraMarca);
    }
  }, [vistaActiva]);

  // =====================================================
  // CÁLCULOS POR VISTA (laboral / personal / mix)
  // =====================================================
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    // --- MIX: combina laboral + personal ---
    if (vistaActiva === "mix") {
      const lab = IPSOS_VESTIMENTA.laboral;
      const per = IPSOS_VESTIMENTA.personal;

      // Frecuencia combinada (factor = compras/año, así que comprasAnuales = personas * factor)
      const allFreqs = [...new Set([...Object.keys(lab.frecuenciaCompra), ...Object.keys(per.frecuenciaCompra)])];
      const desgloseFrecuencia = {};
      let totalComprasAnuales = 0;

      allFreqs.forEach((freq) => {
        const lInfo = lab.frecuenciaCompra[freq];
        const pInfo = per.frecuenciaCompra[freq];
        const lPersonas = lInfo ? Math.round(totalEjecutivos * lInfo.pct) : 0;
        const pPersonas = pInfo ? Math.round(totalEjecutivos * pInfo.pct) : 0;
        const lAnuales = lInfo ? Math.round(lPersonas * lInfo.factor) : 0;
        const pAnuales = pInfo ? Math.round(pPersonas * pInfo.factor) : 0;

        desgloseFrecuencia[freq] = {
          label: (lInfo || pInfo).label,
          comprasLabAnuales: lAnuales,
          comprasPerAnuales: pAnuales,
          comprasAnuales: lAnuales + pAnuales,
        };
        totalComprasAnuales += lAnuales + pAnuales;
      });

      // Mercados individuales (anuales) para ponderar marcas
      const labComprasAnuales = Object.entries(lab.frecuenciaCompra).reduce((s, [, info]) => {
        const p = Math.round(totalEjecutivos * info.pct);
        return s + Math.round(p * info.factor);
      }, 0);
      const perComprasAnuales = Object.entries(per.frecuenciaCompra).reduce((s, [, info]) => {
        const p = Math.round(totalEjecutivos * info.pct);
        return s + Math.round(p * info.factor);
      }, 0);
      const labMercadoAnual = labComprasAnuales * ticketPromedio;
      const perMercadoAnual = perComprasAnuales * ticketPromedio;
      const mercadoAnual = totalComprasAnuales * ticketPromedio;
      const mercadoMensual = mercadoAnual / 12;
      const totalComprasMensuales = totalComprasAnuales / 12;

      // Merge marcas (mercado anual por marca)
      const brandMap = {};
      Object.entries(lab.marcas).forEach(([n, sh]) => {
        brandMap[n] = (brandMap[n] || 0) + Math.round(labMercadoAnual * sh);
      });
      Object.entries(per.marcas).forEach(([n, sh]) => {
        brandMap[n] = (brandMap[n] || 0) + Math.round(perMercadoAnual * sh);
      });
      const marketShareData = Object.entries(brandMap)
        .sort(([, a], [, b]) => b - a)
        .map(([nombre, mercadoAnualMarca]) => ({
          nombre,
          share: mercadoAnual ? mercadoAnualMarca / mercadoAnual : 0,
          clientes: mercadoAnual ? Math.round(totalEjecutivos * (mercadoAnualMarca / mercadoAnual)) : 0,
          mercadoAnual: mercadoAnualMarca,
          mercadoMensual: Math.round(mercadoAnualMarca / 12),
        }));

      // Merge dónde compra (promedio)
      const dcMap = {};
      Object.entries(lab.dondeCompra).forEach(([l, p]) => {
        dcMap[l] = { lab: p, per: 0 };
      });
      Object.entries(per.dondeCompra).forEach(([l, p]) => {
        if (dcMap[l]) dcMap[l].per = p;
        else dcMap[l] = { lab: 0, per: p };
      });
      const dondeCompra = {};
      Object.entries(dcMap).forEach(([l, { lab: lp, per: pp }]) => {
        const avg = (lp + pp) / 2;
        dondeCompra[l] = { personas: Math.round(totalEjecutivos * avg), pct: avg * 100 };
      });

      // Merge canal preferido (promedio)
      const canalPreferido = {};
      const allCanales = { ...lab.canalPreferido, ...per.canalPreferido };
      Object.keys(allCanales).forEach((c) => {
        const avg = ((lab.canalPreferido[c] || 0) + (per.canalPreferido[c] || 0)) / 2;
        canalPreferido[c] = { personas: Math.round(totalEjecutivos * avg), pct: avg * 100 };
      });

      return {
        desgloseFrecuencia,
        totalComprasAnuales,
        totalComprasMensuales,
        mercadoMensual,
        mercadoAnual,
        marketShareData,
        dondeCompra,
        canalPreferido,
        tipoVestimenta: null,
        isMix: true,
      };
    }

    // --- LABORAL / PERSONAL ---
    // factor = compras/año, así que comprasAnuales = personas * factor
    const data = IPSOS_VESTIMENTA[vistaActiva];

    const desgloseFrecuencia = {};
    let totalComprasAnuales = 0;

    Object.entries(data.frecuenciaCompra).forEach(([freq, info]) => {
      const personas = Math.round(totalEjecutivos * info.pct);
      const comprasAnuales = Math.round(personas * info.factor);
      desgloseFrecuencia[freq] = { pct: info.pct, label: info.label, personas, comprasAnuales };
      totalComprasAnuales += comprasAnuales;
    });

    const totalComprasMensuales = totalComprasAnuales / 12;
    const mercadoAnual = totalComprasAnuales * ticketPromedio;
    const mercadoMensual = mercadoAnual / 12;

    const marketShareData = Object.entries(data.marcas)
      .sort(([, a], [, b]) => b - a)
      .map(([nombre, share]) => ({
        nombre,
        share,
        clientes: Math.round(totalEjecutivos * share),
        mercadoAnual: Math.round(mercadoAnual * share),
        mercadoMensual: Math.round((mercadoAnual * share) / 12),
      }));

    const dondeCompra = {};
    Object.entries(data.dondeCompra).forEach(([lugar, pct]) => {
      dondeCompra[lugar] = { personas: Math.round(totalEjecutivos * pct), pct: pct * 100 };
    });

    const canalPreferido = {};
    Object.entries(data.canalPreferido).forEach(([canal, pct]) => {
      canalPreferido[canal] = { personas: Math.round(totalEjecutivos * pct), pct: pct * 100 };
    });

    let tipoVestimenta = null;
    if (data.tipoVestimenta) {
      tipoVestimenta = {};
      Object.entries(data.tipoVestimenta).forEach(([tipo, pct]) => {
        tipoVestimenta[tipo] = { personas: Math.round(totalEjecutivos * pct), pct: pct * 100 };
      });
    }

    return {
      desgloseFrecuencia,
      totalComprasAnuales,
      totalComprasMensuales,
      mercadoMensual,
      mercadoAnual,
      marketShareData,
      dondeCompra,
      canalPreferido,
      tipoVestimenta,
      isMix: false,
    };
  }, [totalEjecutivos, ticketPromedio, vistaActiva]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN
  // =====================================================
  const calcularOportunidad = useCallback(
    (marca, pctCaptacion) => {
      if (!resultados) return null;

      const brandData = resultados.marketShareData.find((d) => d.nombre === marca);
      if (!brandData) return null;

      const brandMercadoMensual = brandData.mercadoMensual;
      const brandMercadoAnual = brandData.mercadoAnual;
      const captacionMensual = Math.round(brandMercadoMensual * (pctCaptacion / 100));
      const captacionAnual = Math.round(brandMercadoAnual * (pctCaptacion / 100));

      return {
        mercadoTotal: resultados.mercadoMensual,
        brandShare: brandData.share,
        brandClientes: brandData.clientes,
        brandMercadoMensual,
        captacionMensual,
        captacionAnual,
      };
    },
    [resultados]
  );

  if (loading || edificios.length === 0) return <Loader />;

  const isMix = vistaActiva === "mix";
  const dataActiva = isMix ? null : IPSOS_VESTIMENTA[vistaActiva];
  const frecuenciaKeys = isMix
    ? [
        ...new Set([
          ...Object.keys(IPSOS_VESTIMENTA.laboral.frecuenciaCompra),
          ...Object.keys(IPSOS_VESTIMENTA.personal.frecuenciaCompra),
        ]),
      ]
    : Object.keys(dataActiva.frecuenciaCompra);

  return (
    <div className={styles.vestimentaPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>VESTIMENTA</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.vestimentaContent}>
        {/* Tabs Laboral / Personal / Mix */}
        <div className={styles.tabsContainer}>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "laboral" && styles.tabActivo)}
            onClick={() => setVistaActiva("laboral")}
          >
            Laboral
          </button>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "personal" && styles.tabActivo)}
            onClick={() => setVistaActiva("personal")}
          >
            Personal
          </button>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "mix" && styles.tabActivo)}
            onClick={() => setVistaActiva("mix")}
          >
            Mix
          </button>
        </div>

        {/* Mercado (toggle mes/año) — primero, controla toda la vista */}
        {resultados && (
          <div className={styles.bloqueMercadoTop}>
            <div className={styles.bloqueMercado}>
              <div className={styles.mercadoLabelRow}>
                <span className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</span>
                <div className={styles.toggleContainer}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                </div>
              </div>
              <div className={styles.mercadoValorGrande}>
                {formatLocal(convertCLP(verAnual ? resultados.mercadoAnual : resultados.mercadoMensual))}
              </div>
            </div>
          </div>
        )}

        {/* Tabla de frecuencia de compra */}
        {resultados && (
          <div className={cx(styles.tablaContainer, isMix && styles.tablaContainerMix)}>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                {isMix ? (
                  <tr>
                    <th className="text-center">Frecuencia</th>
                    <th className="text-center">Laboral</th>
                    <th className="text-center">Personal</th>
                    <th className="text-center">Total {verAnual ? "año" : "mes"}</th>
                  </tr>
                ) : (
                  <tr>
                    <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de compra</th>
                    <th className={cx("text-center", styles.thPersonas)}>Según frecuencia</th>
                    <th className={cx("text-center", styles.thConversiones)}>
                      Conversiones {verAnual ? "año" : "mes"}
                    </th>
                  </tr>
                )}
              </thead>
              <tbody>
                {frecuenciaKeys.map((freq) => {
                  const data = resultados.desgloseFrecuencia[freq];
                  const toView = (anual) => (verAnual ? anual : anual / 12);
                  if (isMix) {
                    return (
                      <tr key={freq}>
                        <td className={styles.celdaFrecuencia}>{data.label}</td>
                        <td className="text-center">
                          {freq === "noCompra" ? "-" : formatNumber(Math.round(toView(data.comprasLabAnuales)))}
                        </td>
                        <td className="text-center">
                          {data.comprasPerAnuales ? formatNumber(Math.round(toView(data.comprasPerAnuales))) : "-"}
                        </td>
                        <td className="text-center">
                          {freq === "noCompra" ? "-" : formatNumber(Math.round(toView(data.comprasAnuales)))}
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={freq}>
                      <td className={styles.celdaFrecuencia}>
                        <div className={styles.frecuenciaInner}>
                          <span className={styles.badgeIpsos}>{(data.pct * 100).toFixed(0)}%</span>
                          <span>{data.label}</span>
                        </div>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                      <td className="text-center">
                        {freq === "noCompra" ? "-" : formatNumber(Math.round(toView(data.comprasAnuales)))}
                      </td>
                    </tr>
                  );
                })}
                <tr className={styles.filaTotales}>
                  <td colSpan={isMix ? 3 : 2} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotal)}>
                    <strong>
                      {formatNumber(
                        Math.round(verAnual ? resultados.totalComprasAnuales : resultados.totalComprasMensuales)
                      )}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Ticket promedio */}
        {resultados && (
          <div className={styles.dosBloquesContainer}>
            <div className={styles.bloqueTicket}>
              <div className={styles.ticketLabel}>Ticket promedio</div>
              <div className={styles.ticketBox}>
                <div className={styles.ticketOpciones}>
                  {TICKETS_VESTIMENTA.map((t) => (
                    <label key={t} className={styles.ticketRadio}>
                      <input
                        type="radio"
                        name="ticketPromedio"
                        checked={ticketPromedio === t}
                        onChange={() => setTicketPromedio(t)}
                      />
                      <span>{formatLocal(convertCLP(t))}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Market Share por Marca */}
        {resultados && (
          <div className={styles.marketShareContainer}>
            <h4 className={styles.marketShareTitulo}>Market Share por Marca</h4>
            <div className={styles.marketShareGrid}>
              {resultados.marketShareData.map((data) => (
                <div key={data.nombre} className={styles.marketShareCard}>
                  <div className={styles.cardNombre}>{data.nombre}</div>
                  <div className={styles.cardShare}>
                    <span className={styles.badgeIpsos}>{(data.share * 100).toFixed(0)}%</span>
                  </div>
                  <div className={styles.cardMetricas}>
                    <div>
                      <span className={styles.metricaLabel}>Clientes</span>
                      <span className={styles.metricaValor}>{formatNumber(data.clientes)}</span>
                    </div>
                    <div>
                      <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                      <span className={styles.metricaValor}>
                        {formatLocal(convertCLP(verAnual ? data.mercadoAnual : data.mercadoMensual))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
              Simular Oportunidad
            </button>
          </div>
        )}

        {/* Tablas de desglose */}
        {resultados && (
          <div className={styles.tablasDesglose}>
            <div className={styles.desgloseContainer}>
              <h4>¿Dónde compra?</h4>
              <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-start">Lugar</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultados.dondeCompra).map(([lugar, data]) => (
                    <tr key={lugar}>
                      <td className="text-start">{lugar}</td>
                      <td className="text-center">
                        <span className={styles.badgeIpsos}>{data.pct.toFixed(0)}%</span>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.desgloseContainer}>
              {resultados.tipoVestimenta && !isMix ? (
                <>
                  <h4>Tipo de vestimenta</h4>
                  <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                    <thead className="table-dark">
                      <tr>
                        <th className="text-start">Tipo</th>
                        <th className="text-center">%</th>
                        <th className="text-center">Personas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(resultados.tipoVestimenta).map(([tipo, data]) => (
                        <tr key={tipo}>
                          <td className="text-start">{tipo}</td>
                          <td className="text-center">
                            <span className={styles.badgeIpsos}>{data.pct.toFixed(0)}%</span>
                          </td>
                          <td className="text-center">{formatNumber(data.personas)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <>
                  <h4>Canal preferido</h4>
                  <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                    <thead className="table-dark">
                      <tr>
                        <th className="text-start">Canal</th>
                        <th className="text-center">%</th>
                        <th className="text-center">Personas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(resultados.canalPreferido).map(([canal, data]) => (
                        <tr key={canal}>
                          <td className="text-start">{canal}</td>
                          <td className="text-center">
                            <span className={styles.badgeIpsos}>{data.pct.toFixed(0)}%</span>
                          </td>
                          <td className="text-center">{formatNumber(data.personas)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        )}

        {/* Canal preferido para laboral y mix (debajo del desglose) */}
        {resultados && (resultados.tipoVestimenta || isMix) && (
          <div className={styles.canalResumen}>
            {Object.entries(resultados.canalPreferido).map(([canal, data]) => (
              <span key={canal}>
                <span className={styles.badgeIpsos}>{data.pct.toFixed(0)}%</span> {canal}
                {" — "}
                <strong>{formatNumber(data.personas)}</strong> personas
              </span>
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos.
            {isMix
              ? " Vista combinada laboral + personal. Mercados y conversiones sumados de ambas categorías."
              : ` Vestimenta ${vistaActiva}: el ${vistaActiva === "laboral" ? "39.8%" : "53.6%"} compra mensualmente.`}
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(
                    ({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork, created_at }) => (
                      <tr
                        key={created_at}
                        onClick={() => toggleCheck(id)}
                        className={cx(checkedBuildings[id] && "table-success")}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="text-center">
                          {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                        </td>
                        <td className="text-center">{edificio.toUpperCase()}</td>
                        <td className="text-center">{ubicacion}</td>
                        <td className="text-center">
                          {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                        </td>
                        <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && resultados && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  <div className={styles.controlGroup}>
                    <label>Marca objetivo:</label>
                    <select
                      value={marcaSeleccionada}
                      onChange={(e) => setMarcaSeleccionada(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      {(isMix ? resultados.marketShareData.map((d) => d.nombre) : Object.keys(dataActiva.marcas)).map(
                        (m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>% captación mercado:</label>
                    <select
                      value={porcentajeCaptacion}
                      onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                      className="form-select form-select-sm"
                    >
                      {ESCENARIOS_CAPTACION.map((p) => (
                        <option key={p} value={p}>
                          {p}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const oportunidad = calcularOportunidad(marcaSeleccionada, porcentajeCaptacion);
                  if (!oportunidad) return null;

                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado total mensual</span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>
                          {marcaSeleccionada} — {(oportunidad.brandShare * 100).toFixed(0)}% share (
                          {formatNumber(oportunidad.brandClientes)} clientes)
                        </span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.brandMercadoMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>
                          Captación {porcentajeCaptacion}% de {marcaSeleccionada}
                        </span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.captacionMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Oportunidad anual</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.captacionAnual))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vestimenta;
